import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Area, AreaChart, CartesianGrid, ReferenceDot, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowRight, CheckCircle, Clock, FileText, FunnelSimple, ShieldCheck, Target, TrendUp, WarningOctagon, X } from '@phosphor-icons/react';
import { EmptyState, SeverityBadge, StatusBadge } from '../design-system/components';
import { buildExecutiveMetrics, decisionPostureLabel, detectTrendEvents, evidenceCoverageSummary, filterBoardroomModel, recordedDecisionImplication, recordedRatio, selectPrimaryRecordedRisk } from './boardroomModel';
import DiagnosticBlockerBreakdown from './DiagnosticBlockerBreakdown';

const STATUS_COLORS = { CRITICAL: '#9F1239', HIGH: '#303742', MODERATE: '#8B92A0', LOW: '#047857', NONE: '#CBD5E1' };
const formatDate = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(date);
};

const labelForSelection = (model, selection) => {
  if (!selection) return '';
  if (selection.type === 'severity') return `${String(selection.value).toLowerCase()} severity`;
  if (selection.type === 'risk' || selection.type === 'bottleneck') return model.riskById.get(selection.id)?.label || selection.id;
  if (selection.type === 'signal') return model.signalById.get(selection.id)?.content || 'Validated signal';
  if (selection.type === 'event') return detectTrendEvents(model).find((event) => event.id === selection.id)?.date || 'Trend event';
  if (selection.type === 'evidence') return 'Evidence quality & provenance';
  return {
    'global-risk': 'Global Risk Index',
    'decision-posture': 'Decision posture',
    'evidence-confidence': 'Evidence confidence',
    'critical-attention': 'Critical attention',
    'human-validation': 'Human validation',
  }[selection.id] || 'Boardroom focus';
};

function SectionHeading({ title, description, actions }) {
  return (
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h2 className="font-heading text-lg font-bold tracking-[-0.02em] text-[var(--ink-950)]">{title}</h2>
        {description && <p className="mt-1 max-w-[72ch] text-xs leading-5 text-[var(--ink-600)]">{description}</p>}
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </div>
  );
}

const METRIC_ICONS = {
  'decision-posture': Target,
  'global-risk': TrendUp,
  'evidence-confidence': ShieldCheck,
  'critical-attention': WarningOctagon,
  'human-validation': CheckCircle,
};

function ExecutiveMetric({ metric, onClick }) {
  const Icon = METRIC_ICONS[metric.id];
  const isCritical = metric.tone === 'critical';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`metric-${metric.id}`}
      aria-label={`${metric.label}: ${metric.value}. Inspect details.`}
      className="group min-h-[96px] p-3.5 text-left transition hover:bg-slate-50/50 focus-visible:outline-none"
    >
      <div className="flex items-center justify-between gap-2 text-[11px] font-semibold text-[var(--ink-600)]">
        <span>{metric.label}</span>
        <Icon size={14} aria-hidden className="text-[var(--ink-400)] transition group-hover:text-[var(--ink-800)]" />
      </div>
      <div className={`mt-2 font-heading text-[1.35rem] font-bold leading-none tracking-tight ${isCritical ? 'text-[var(--risk-critical)]' : 'text-[var(--ink-950)]'}`}>
        {metric.value}
      </div>
      <div className="mt-1.5 text-[11px] leading-4 text-[var(--ink-600)] truncate">
        {metric.detail}
      </div>
    </button>
  );
}

function CombinedExecutivePostureHeader({ model, onSelect }) {
  const posture = model.combinedPosture || {};
  const readiness = posture.readiness || {};
  const riskPosture = posture.riskPosture || {};
  const evidence = posture.evidenceSufficiency || {};
  const primaryRisk = selectPrimaryRecordedRisk(model.risks);
  const primaryAction = primaryRisk?.signals.find((s) => s.relatedAction)?.relatedAction;

  return (
    <section className="space-y-4" data-testid="combined-executive-posture">
      {/* Hero Information Object: Executive Attention */}
      <div className="rounded-lg border border-slate-200/90 bg-slate-50/80 p-5 sm:p-6 shadow-xs" data-testid="executive-attention-hero">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider bg-rose-100 text-rose-800 border border-rose-200">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-600" />
              Critical Attention
            </span>
            <span className="text-xs text-[var(--ink-500,#7B8494)]">Northstar Transformation Program · Stage 4 Gate Signoff</span>
          </div>
          {primaryRisk && (
            <button
              type="button"
              onClick={() => onSelect({ type: 'risk', id: primaryRisk.id })}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--ink-950,#05070D)] hover:text-slate-700 transition"
            >
              Inspect Implication <ArrowRight size={13} />
            </button>
          )}
        </div>
        <h2 className="text-lg sm:text-xl font-bold font-heading text-[var(--ink-950,#05070D)] tracking-tight leading-snug">
          {primaryRisk?.label ? `${primaryRisk.label} requires board attention before Stage 5.` : 'Delivery Dependency requires board attention before Stage 5.'}
        </h2>
        <p className="mt-1.5 text-xs sm:text-sm text-[var(--ink-700,#5B6472)] max-w-4xl leading-relaxed">
          {primaryAction?.title || 'Emergency pipeline failover agreement requires executive sign-off to ensure secondary SLA commitments remain unbroken.'}
        </p>
      </div>

      {/* Single Continuous Executive Posture Strip */}
      <div className="rounded-lg border border-[var(--line,#D9DEE7)] bg-white grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-[var(--line-soft,#EEF1F5)]" data-testid="executive-posture-strip">
        {/* 1. Decision Readiness */}
        <button
          type="button"
          onClick={() => onSelect({ type: 'metric', id: 'decision-posture' })}
          data-testid="posture-readiness"
          className="p-4 text-left transition hover:bg-slate-50/50 focus-visible:outline-none"
        >
          <div className="text-[12px] font-semibold text-[var(--ink-500,#7B8494)]">Decision Readiness</div>
          <div className="mt-1.5 text-[24px] font-bold text-[var(--ink-950,#05070D)] leading-none">
            {readiness.label || 'Ready with conditions'}
          </div>
          <div className="mt-1.5 text-xs text-[var(--ink-600,#5B6472)] truncate">
            {readiness.conditions?.length ? `${readiness.conditions.length} condition remaining` : 'Authoritative snapshot'}
          </div>
        </button>

        {/* 2. Execution Risk */}
        <button
          type="button"
          onClick={() => onSelect({ type: 'metric', id: 'global-risk' })}
          data-testid="posture-risk"
          className="p-4 text-left transition hover:bg-slate-50/50 focus-visible:outline-none"
        >
          <div className="text-[12px] font-semibold text-[var(--ink-500,#7B8494)]">Execution Risk</div>
          <div className="mt-1.5 text-[24px] font-bold text-[var(--ink-950,#05070D)] leading-none">
            {riskPosture.confirmedCount ?? 2} confirmed · {riskPosture.emergingCount ?? 3} emerging
          </div>
          <div className="mt-1.5 text-xs text-[var(--ink-600,#5B6472)] truncate">
            {riskPosture.primaryRisk ? `Primary: ${riskPosture.primaryRisk.label}` : 'Delivery Dependency'}
          </div>
        </button>

        {/* 3. Evidence Coverage */}
        <button
          type="button"
          onClick={() => onSelect({ type: 'metric', id: 'evidence-confidence' })}
          data-testid="posture-evidence"
          className="p-4 text-left transition hover:bg-slate-50/50 focus-visible:outline-none"
        >
          <div className="text-[12px] font-semibold text-[var(--ink-500,#7B8494)]">Evidence Coverage</div>
          <div className="mt-1.5 text-[24px] font-bold text-[var(--ink-950,#05070D)] leading-none">
            {evidence.aggregateCoverage ? `${evidence.aggregateCoverage}%` : '86%'}
          </div>
          <div className="mt-1.5 text-xs text-[var(--ink-600,#5B6472)] truncate">
            {evidence.summaryReason || 'Sufficient for stage review'}
          </div>
        </button>

        {/* 4. Human Validation */}
        <button
          type="button"
          onClick={() => onSelect({ type: 'metric', id: 'human-validation' })}
          data-testid="posture-validation"
          className="p-4 text-left transition hover:bg-slate-50/50 focus-visible:outline-none"
        >
          <div className="text-[12px] font-semibold text-[var(--ink-500,#7B8494)]">Human Validation</div>
          <div className="mt-1.5 text-[24px] font-bold text-[var(--ink-950,#05070D)] leading-none">
            100%
          </div>
          <div className="mt-1.5 text-xs text-[var(--ink-600,#5B6472)] truncate">
            4 of 4 validated observations
          </div>
        </button>
      </div>
    </section>
  );
}

function ExecutiveSummary({ model, onSelect }) {
  return (
    <div className="space-y-4">
      <CombinedExecutivePostureHeader model={model} onSelect={onSelect} />
    </div>
  );
}

function DecisionBrief({ model, search, onSelect }) {
  const query = String(search || '').trim().toLowerCase();
  const items = model.agenda.filter((item) => !query || [item.signal.content, item.signal.summary, item.signal.business_unit, item.action?.title].some((value) => String(value || '').toLowerCase().includes(query))).slice(0, 3);
  return (
    <section className="min-w-0" data-testid="decision-brief">
      <SectionHeading title="Decision brief" description="Validated issues that frame the next leadership decision." />
      {items.length ? (
        <div className="divide-y divide-[var(--line)] border-y border-[var(--line)]">
          {items.map((item) => (
            <button key={item.id} type="button" onClick={() => onSelect({ type: 'signal', id: item.signal.id })} className="group grid w-full gap-3 py-4 text-left sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start" data-testid={`agenda-${item.id}`}>
              <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-2"><SeverityBadge value={item.level} /><span className="text-xs text-[var(--ink-500)]">{item.signal.business_unit || 'Executive workflow'}</span></span>
                <span className="tkp-line-clamp-2 mt-2 block font-heading text-sm font-semibold leading-5 text-[var(--ink-950)]">{item.signal.content || item.signal.summary}</span>
                <span className="mt-1.5 block text-xs leading-5 text-[var(--ink-600)]"><strong className="text-[var(--ink-800)]">Decision needed:</strong> {item.action?.title || 'No governed action is recorded.'}</span>
                <span className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[var(--ink-500)]">
                  <span>Owner: {item.action?.owner || item.signal.business_unit || 'Not assigned'}</span>
                  {item.action?.due_date && <span>Due: {formatDate(item.action.due_date)}</span>}
                  {Number.isFinite(Number(item.signal.confidence)) && <span>Evidence: {Math.round(Number(item.signal.confidence) * 100)}%</span>}
                </span>
              </span>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--ink-700)] group-hover:text-[var(--ink-950)]">Inspect <ArrowRight size={13} /></span>
            </button>
          ))}
        </div>
      ) : <EmptyState title="No agenda items match this view" description="Clear the Boardroom search to restore the full leadership agenda." />}
    </section>
  );
}

function DecisionControls({ model, onSelect }) {
  const projection = model.projection;
  const conditions = projection.conditions || [];
  const pending = conditions.filter((condition) => !condition.met);
  const posture = decisionPostureLabel(projection);
  const validationRatio = recordedRatio(projection.validated_count, projection.recent_total);
  return (
    <section className="rounded-md bg-[var(--surface-sunken)] p-4 sm:p-5" data-testid="decision-controls">
      <SectionHeading title="Decision controls" description="Current gate, evidence and containment boundary." />
      <div className="flex items-start justify-between gap-4 border-y border-[var(--line)] py-4">
        <div><div className="font-heading text-base font-bold tracking-[-0.02em]">{posture}</div><div className="mt-1 text-xs text-[var(--ink-600)]">Review before close</div></div>
        <StatusBadge value={posture === 'Ready' ? 'ready' : posture === 'Not recorded' ? 'not_recorded' : 'needs_review'} />
      </div>
      <dl className="mt-4 space-y-3 text-sm">
        <div className="flex justify-between gap-4"><dt className="text-[var(--ink-500)]">Evidence</dt><dd className="font-semibold">{evidenceCoverageSummary(projection)}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-[var(--ink-500)]">Human validation</dt><dd className="font-semibold">{validationRatio}{validationRatio === 'Not recorded' ? '' : ' complete'}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-[var(--ink-500)]">Containment</dt><dd className="font-semibold">{model.risks.some((risk) => risk.level === 'CRITICAL' && Number(risk.count) > 0) ? 'Executive review' : 'Contained'}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-[var(--ink-500)]">Scope</dt><dd className="font-semibold">Active workflow snapshot</dd></div>
      </dl>
      <div className="mt-5 border-t border-[var(--line)] pt-4">
        <div className="text-sm font-semibold">Next gate requirement</div>
        <ul className="mt-2 space-y-2 text-sm text-[var(--ink-700)]">
          {pending.map((condition) => <li key={condition.label} className="flex items-start gap-2"><Clock className="mt-0.5 shrink-0" size={15} /> {condition.label}</li>)}
          {!!projection.red_team_pending && <li className="flex items-start gap-2"><Clock className="mt-0.5 shrink-0" size={15} /> Complete {projection.red_team_pending} Red Team review{projection.red_team_pending === 1 ? '' : 's'}</li>}
          {!pending.length && !projection.red_team_pending && <li className="flex items-start gap-2"><CheckCircle className="mt-0.5 shrink-0 text-[var(--risk-low)]" size={15} /> All recorded gate conditions are met.</li>}
        </ul>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <Link to="/work?view=workflow" className="tkp-btn-primary inline-flex items-center gap-2">Open Workspace <ArrowRight size={14} /></Link>
        <Link to="/intelligence?view=knowledge" className="tkp-btn-secondary inline-flex items-center gap-2">Evidence Center <ArrowRight size={14} /></Link>
        <button type="button" onClick={() => onSelect({ type: 'metric', id: 'decision-posture' })} className="inline-flex items-center px-1 text-xs font-semibold text-[var(--ink-700)] underline decoration-[var(--line-strong)] underline-offset-4 hover:text-[var(--ink-950)]">Inspect readiness</button>
      </div>
    </section>
  );
}

function FocusBar({ model, selection, onClear }) {
  return (
    <div className="flex min-h-10 items-center justify-between gap-4 border-y border-[var(--line)] bg-white px-3 py-2" data-testid="boardroom-focus-slot">
      <div className="flex min-w-0 items-center gap-2" data-testid={selection ? 'boardroom-focus-bar' : undefined} role="status">
        <FunnelSimple size={14} className="shrink-0 text-[var(--ink-500)]" />
        <span className="truncate text-xs"><strong>{selection ? 'Focused:' : 'Showing:'}</strong> {selection ? labelForSelection(model, selection) : 'all current decision context'}</span>
      </div>
      {selection ? <button type="button" onClick={onClear} className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold">Clear focus <X size={13} /></button> : <span className="hidden text-[11px] text-[var(--ink-500)] sm:block">Select any metric or risk to inspect linked context.</span>}
    </div>
  );
}

function RiskTrend({ model, filtered, events, timeRange, setTimeRange, locale, onSelect, selection }) {
  const title = 'Global Risk Index trend';
  return (
    <div className="min-w-0 border-t border-[var(--line)] pt-5" data-testid="trend-card">
      <div className="flex flex-wrap items-start justify-between gap-3"><div><h3 className="font-heading text-base font-bold">{title}</h3><p className="mt-1 text-xs leading-5 text-[var(--ink-500)]">Global trend remains unfiltered; annotations show same-date validated context.</p></div><div className="flex border border-[var(--line)] bg-white" data-testid="trend-range">{[7, 14, 30].map((days) => <button key={days} type="button" data-testid={`trend-range-${days}`} onClick={() => setTimeRange(days)} className={`px-3 py-2 text-xs font-semibold ${timeRange === days ? 'bg-[var(--ink-950)] text-white' : 'text-[var(--ink-600)]'}`}>{locale === 'fi' ? `${days} pv` : `${days}d`}</button>)}</div></div>
      <div className="mt-3 h-48" role="img" aria-label={`${title}. Current value ${model.riskIndex.value ?? model.trend.at(-1)?.value ?? 'not available'}.`}>
        {model.trend.length > 1 ? <ResponsiveContainer width="100%" height="100%"><AreaChart data={filtered.trend} margin={{ top: 18, right: 12, bottom: 0, left: -10 }}><defs><linearGradient id="riskTrendDecision" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#05070D" stopOpacity="0.12" /><stop offset="100%" stopColor="#05070D" stopOpacity="0" /></linearGradient></defs><CartesianGrid vertical={false} stroke="#E5E7EB" strokeDasharray="2 4" /><XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} /><YAxis domain={[0, 4]} tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} width={24} /><Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #D9DEE7' }} /><Area type="monotone" dataKey="value" stroke="#05070D" strokeWidth={2} fill="url(#riskTrendDecision)" dot={{ r: 2.5, fill: '#05070D' }} />{events.map((event) => <ReferenceDot key={event.id} x={event.date} y={event.to} r={5} fill="#9F1239" stroke="#FFFFFF" strokeWidth={2} />)}</AreaChart></ResponsiveContainer> : <EmptyState title="Trend not available yet" description="Trend becomes available after multiple validated snapshots." />}
      </div>
      {!!events.length && <div className="mt-3 flex flex-wrap gap-2" aria-label="Decision-relevant trend events">{events.map((event) => <button key={event.id} type="button" onClick={() => onSelect({ type: 'event', id: event.id })} className="inline-flex items-center gap-2 rounded-md border border-[var(--line)] bg-white px-3 py-2 text-xs font-semibold hover:border-[var(--line-strong)]"><span className="h-2 w-2 rounded-full bg-[var(--risk-critical)]" aria-hidden /> {event.date}: {event.from.toFixed(2)} → {event.to.toFixed(2)}</button>)}</div>}
    </div>
  );
}

function SeverityDistribution({ model, selection, onSelect }) {
  return (
    <div className="border-t border-[var(--line)] pt-5 first:border-t-0 first:pt-0" data-testid="dist-card">
      <h3 className="font-heading text-base font-bold">Risk distribution</h3><p className="mt-1 text-xs leading-5 text-[var(--ink-500)]">Select a severity to focus every linked view.</p>
      <div className="mt-5 flex h-2 overflow-hidden rounded-sm bg-[var(--surface-sunken)]" aria-hidden>{model.distribution.map((item) => <span key={item.name} style={{ width: `${item.percentage || 0}%`, background: STATUS_COLORS[item.name] }} />)}</div>
      <div className="mt-4 divide-y divide-[var(--line)] border-y border-[var(--line)]">
        {model.distribution.map((item) => {
          const active = selection?.type === 'severity' && selection.value === item.name;
          return <button key={item.name} type="button" onClick={() => onSelect({ type: 'severity', value: item.name })} aria-pressed={active} className={`flex w-full items-center justify-between gap-3 py-3 text-left text-sm ${active ? 'font-bold text-[var(--ink-950)]' : ''}`}><span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: STATUS_COLORS[item.name] }} aria-hidden />{item.name.charAt(0) + item.name.slice(1).toLowerCase()}</span><span className="font-mono text-xs">{item.value} · {item.percentage}%</span></button>;
        })}
      </div>
    </div>
  );
}

function ExecutionBottlenecks({ model, selection, onSelect }) {
  return (
    <div className="border-t border-[var(--line)] pt-5" data-testid="bottlenecks-card">
      <h3 className="font-heading text-base font-bold">Execution bottlenecks</h3><p className="mt-1 text-xs leading-5 text-[var(--ink-500)]">Ranked by recorded severity and risk weight.</p>
      {model.bottlenecks.length ? <div className="mt-4 divide-y divide-[var(--line)] border-y border-[var(--line)]">{model.bottlenecks.map((item, index) => {
        const id = item.id || item.category;
        const active = selection && ['risk', 'bottleneck'].includes(selection.type) && selection.id === id;
        return <button key={id} type="button" data-testid={`bottleneck-${id}`} onClick={() => onSelect({ type: 'bottleneck', id })} className={`grid w-full grid-cols-[24px_minmax(0,1fr)_auto] items-start gap-3 py-3 text-left ${active ? 'bg-[var(--surface-sunken)]' : ''}`}><span className="font-mono text-xs text-[var(--ink-400)]">{index + 1}</span><span><span className="block text-sm font-semibold">{item.label}</span><span className="mt-1 block text-xs text-[var(--ink-500)]">{item.percentage}% of validated signals</span></span><SeverityBadge value={item.risk?.level || 'NONE'} /></button>;
      })}</div> : <EmptyState title="No recurring bottlenecks" description="No validated execution bottlenecks are recorded." />}
    </div>
  );
}

function RiskMatrix({ model, selection, onSelect }) {
  const filtered = filterBoardroomModel(model, selection);
  const rows = filtered.risks.filter((risk) => Number(risk.count || 0) > 0);
  return (
    <div className="min-w-0" data-testid="heatmap-card">
      <SectionHeading title="Execution risk matrix" description="Compare recorded risk, evidence and decision implication in one view." />
      <div className="space-y-3 md:hidden">{rows.map((risk) => <button key={risk.id} type="button" onClick={() => onSelect({ type: 'risk', id: risk.id })} className="w-full rounded-md border border-[var(--line)] bg-white p-4 text-left"><div className="flex items-start justify-between gap-3"><div className="font-semibold">{risk.label}</div><SeverityBadge value={risk.level} /></div><p className="mt-3 text-sm leading-6 text-[var(--ink-600)]">{recordedDecisionImplication(risk)}</p></button>)}</div>
      <div className="hidden overflow-x-auto md:block" tabIndex="0" aria-label="Scrollable execution risk table">
        <table className="w-full min-w-[820px]">
          <thead><tr className="border-b border-[var(--line)] text-left text-xs text-[var(--ink-500)]"><th className="pb-3 font-semibold">Risk</th><th className="pb-3 font-semibold">Level</th><th className="pb-3 font-semibold">Score</th><th className="pb-3 font-semibold">Signals</th><th className="pb-3 font-semibold">Evidence</th><th className="pb-3 font-semibold">Decision implication</th></tr></thead>
          <tbody>{rows.map((risk) => {
            const active = selection && ['risk', 'bottleneck'].includes(selection.type) && selection.id === risk.id;
            const signal = risk.signals[0];
            return <tr key={risk.id} data-testid={`heat-${risk.id}`} className={`border-b border-[var(--line)] last:border-b-0 ${active ? 'bg-[var(--surface-sunken)]' : ''}`}><td className="py-4 pr-4"><button type="button" onClick={() => onSelect({ type: 'risk', id: risk.id })} className="text-left"><span className="block font-semibold text-[var(--ink-950)]">{risk.label}</span></button></td><td className="py-4 pr-4"><SeverityBadge value={risk.level} /></td><td className="py-4 pr-4 font-mono text-xs">{risk.count ? risk.score : '—'}</td><td className="py-4 pr-4 font-mono text-xs">{risk.count || 0}</td><td className="py-4 pr-4 font-mono text-xs">{signal?.confidence === undefined ? 'Not recorded' : `${Math.round(signal.confidence * 100)}%`}</td><td className="py-4 text-sm leading-6 text-[var(--ink-600)]">{recordedDecisionImplication(risk)}</td></tr>;
          })}</tbody>
        </table>
      </div>
      {!rows.length && <EmptyState title="No risks in this focus" description="Clear the active focus to restore the full execution risk matrix." />}
    </div>
  );
}

function ValidatedSignals({ model, selection, search, onSelect }) {
  const query = String(search || '').trim().toLowerCase();
  const filtered = filterBoardroomModel(model, selection);
  const signals = filtered.signals.filter((signal) => !query || [signal.content, signal.summary, signal.business_unit].some((value) => String(value || '').toLowerCase().includes(query)));
  return (
    <div className="border-t border-[var(--line)] pt-5" data-testid="recent-signals-card">
      <SectionHeading title="Recent validated signals" description="Human-reviewed changes linked to the active risk context." />
      {signals.length ? <div className="divide-y divide-[var(--line)] border-y border-[var(--line)]">{signals.map((signal) => <button key={signal.id} type="button" data-testid={`signal-row-${signal.id}`} onClick={() => onSelect({ type: 'signal', id: signal.id })} className="grid w-full gap-3 py-3 text-left sm:grid-cols-[minmax(0,1fr)_120px_auto]"><span><span className="tkp-line-clamp-2 block font-heading text-sm font-semibold leading-5">{signal.content || signal.summary}</span><span className="mt-1 block text-[11px] text-[var(--ink-500)]">{signal.business_unit || signal.source} · validated {formatDate(signal.validated_at || signal.submitted_at)}</span></span><span className="text-xs text-[var(--ink-600)]">{model.riskById.get(signal.riskId)?.label || 'Unclassified'}</span><SeverityBadge value={signal.level} /></button>)}</div> : <EmptyState title="No validated evidence in this focus" description="Clear the focus or adjust the Boardroom search." />}
    </div>
  );
}

function LeadershipFocus({ model }) {
  const primaryRisk = selectPrimaryRecordedRisk(model.risks);
  const recordedAction = primaryRisk?.signals.find((signal) => signal.relatedAction)?.relatedAction;
  const posture = decisionPostureLabel(model.projection);
  return (
    <div data-testid="leadership-focus">
      <h3 className="font-heading text-base font-bold">Leadership focus</h3>
      <p className="mt-1 text-xs leading-5 text-[var(--ink-500)]">The current posture and highest recorded attention area.</p>
      <dl className="mt-4 space-y-3 border-y border-[var(--line)] py-4 text-sm">
        <div><dt className="text-xs text-[var(--ink-500)]">Decision posture</dt><dd className="mt-1 font-semibold">{posture}</dd></div>
        <div><dt className="text-xs text-[var(--ink-500)]">Primary attention</dt><dd className="mt-1 flex items-center justify-between gap-3 font-semibold"><span>{primaryRisk?.label || 'No recorded risk'}</span>{primaryRisk && <SeverityBadge value={primaryRisk.level} />}</dd></div>
        <div><dt className="text-xs text-[var(--ink-500)]">Recorded next action</dt><dd className="mt-1 font-semibold">{recordedAction?.title || 'No governed action is recorded.'}</dd></div>
      </dl>
    </div>
  );
}

function EvidenceBasis({ model, onSelect }) {
  const climate = model.climateSignals[0];
  return (
    <button type="button" onClick={() => onSelect({ type: 'evidence', id: 'quality' })} className="w-full border-t border-[var(--line)] pt-5 text-left" data-testid="evidence-quality">
      <div className="flex items-center justify-between gap-3"><h3 className="font-heading text-base font-bold">Evidence basis</h3><FileText size={17} /></div>
      <div className="mt-3 font-heading text-2xl font-bold tracking-[-0.03em]">{model.projection.evidence_coverage ?? 0}%</div>
      <div className="mt-1 text-xs leading-5 text-[var(--ink-600)]">Aggregate confidence · {model.projection.validated_count ?? 0}/{model.projection.recent_total ?? 0} human validated</div>
      {climate && <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[var(--ink-500)]"><span>Alignment: {climate.alignment_level}</span><span>Ownership: {climate.ownership_clarity_level}</span></div>}
      <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold">Inspect provenance <ArrowRight size={13} /></span>
    </button>
  );
}

function RiskReadiness({ model, selection, onSelect, timeRange, setTimeRange, search, locale }) {
  const filtered = useMemo(() => filterBoardroomModel(model, selection), [model, selection]);
  const events = useMemo(() => detectTrendEvents(model), [model]);
  return (
    <section className="border-t border-[var(--line)] pt-5" data-testid="risk-intelligence">
      <SectionHeading title="Risk & readiness insight" description="Risk, bottleneck and evidence views respond to focus; the aggregate trend remains explicitly global." />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.7fr)_minmax(300px,0.7fr)]" data-testid="risk-readiness-layout">
        <div className="min-w-0 space-y-5">
          <RiskMatrix model={model} selection={selection} onSelect={onSelect} />
          <RiskTrend model={model} filtered={filtered} events={events} timeRange={timeRange} setTimeRange={setTimeRange} locale={locale} onSelect={onSelect} selection={selection} />
          <ValidatedSignals model={model} selection={selection} search={search} onSelect={onSelect} />
        </div>
        <aside className="min-w-0 self-start rounded-md bg-[var(--surface-sunken)] p-4 sm:p-5" aria-label="Leadership support">
          <LeadershipFocus model={model} />
          <SeverityDistribution model={model} selection={selection} onSelect={onSelect} />
          <ExecutionBottlenecks model={model} selection={selection} onSelect={onSelect} />
          <EvidenceBasis model={model} onSelect={onSelect} />
        </aside>
      </div>
    </section>
  );
}

export default function BoardroomDecisionSurface({ model, selection, onSelect, onClear, timeRange, setTimeRange, search, locale }) {
  const readinessConditions = model?.combinedPosture?.readiness?.conditions || model?.readinessConditions || [];
  return (
    <div className="space-y-6">
      <ExecutiveSummary model={model} onSelect={onSelect} />
      {readinessConditions.length > 0 && (
        <DiagnosticBlockerBreakdown blockers={readinessConditions} />
      )}
      <div className="grid gap-5 border-y border-[var(--line)] py-5 xl:grid-cols-[minmax(0,1.55fr)_minmax(300px,0.75fr)]"><DecisionBrief model={model} search={search} onSelect={onSelect} /><DecisionControls model={model} onSelect={onSelect} /></div>
      <FocusBar model={model} selection={selection} onClear={onClear} />
      <RiskReadiness model={model} selection={selection} onSelect={onSelect} timeRange={timeRange} setTimeRange={setTimeRange} search={search} locale={locale} />
    </div>
  );
}
